import React from "react";
import { useNavigate } from 'react-router-dom';
import './App.css';
import { useEffect, useState, useRef } from 'react'
import { get } from '@aws-amplify/api';
import AppLayout from './components/AppLayout';

const MarketDirection = ({ signOut }) => {

  const [forceRender, setForceRender] = useState(true);
  const [data, setData] = useState({ security: "", days: "30" });
  const [error, setError] = useState('');
  const [displayMessage, setDisplayMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [marketSentiment, setMarketSentiment] = useState(null);

  let security = useRef();
  let days = useRef();
  let optionsData = useRef([]);
  let lastSale = useRef(0);

  const navigate = useNavigate();

  // Initialize refs with default values
  useEffect(() => {
    days.current = "30";
  }, []);

  useEffect(() => {
    if (submitted) {
      fetchOptionsData();
    }
  }, [submitted]);

  const fetchOptionsData = async () => {
    console.log("Executing fetchOptionsData");
    const myAPI = "mktappapi";
    let path = '/getIronCondorOptionsLegs';

    try {
      setDisplayMessage("Fetching market direction data...");

      // Call API to get options data
      // Using LONG position with wide bounds to get full options chain
      // Upper bound: 5, Lower bound: 0.1, Profit alert: 0, Contracts: 1
      var passApiStr = path + "/LONG/" + data.days + "/" + data.security.toUpperCase().trim() + "/5/0.1/0/1";
      console.log("fetchOptionsData : passApiStr = " + passApiStr);

      const response = await get({
        apiName: myAPI,
        path: passApiStr,
      }).response;

      let resp = await response.body.json();
      console.log("fetchOptionsData : resp = ", resp);
      console.log("fetchOptionsData : resp type = ", typeof resp);

      // Check if response is a string that needs parsing
      if (typeof resp === 'string') {
        console.log("fetchOptionsData : Response is a string, attempting to parse");
        // Replace single quotes with double quotes for valid JSON
        resp = resp.replace(/'/g, '"');
        // Replace " : " with ":" and other spacing issues
        resp = resp.replace(/ : /g, ':');
        // Replace NaN with 0
        resp = resp.replace(/NaN/g, '0');
        // Replace unquoted string values like LONG with "LONG"
        resp = resp.replace(/:\s*([A-Z]+)\s*([,}])/g, ':"$1"$2');
        try {
          resp = JSON.parse(resp);
          console.log("fetchOptionsData : Successfully parsed string to object");
        } catch (parseError) {
          console.error("fetchOptionsData : Failed to parse response string", parseError);
          setError("Failed to parse API response for " + data.security);
          setDisplayMessage("");
          setSubmitted(false);
          return;
        }
      }

      console.log("fetchOptionsData : resp.Details exists = ", !!resp.Details);
      console.log("fetchOptionsData : resp['Last Sale'] exists = ", !!resp["Last Sale"]);

      if (resp && resp.Details && resp["Last Sale"]) {
        optionsData.current = resp.Details;
        lastSale.current = resp["Last Sale"];

        console.log("fetchOptionsData : optionsData.current length = ", optionsData.current.length);
        console.log("fetchOptionsData : lastSale.current = ", lastSale.current);

        // Analyze market direction
        const sentiment = analyzeBearBull(optionsData.current, lastSale.current);
        setMarketSentiment(sentiment);
        setDisplayMessage("");
        setError("");
      } else {
        console.log("fetchOptionsData : Response missing required fields");
        console.log("fetchOptionsData : Full response = ", JSON.stringify(resp));
        setError("Unable to fetch options data for " + data.security + ". Response missing Details or Last Sale field.");
        setDisplayMessage("");
      }
      setSubmitted(false);
    } catch (error) {
      setError("Error fetching data: " + error.message);
      setDisplayMessage("");
      setSubmitted(false);
      console.log("fetchOptionsData : Error = ", error.message);
      console.error("fetchOptionsData : Full error = ", error);
    }
  };

  // Copy all analysis functions from IronCondorOptionsLegs
  function analyzeBearBull(optionsLegsDetails, lastSale) {
    console.log("Executing analyzeBearBull");
    console.log("analyzeBearBull : optionsLegsDetails = ", optionsLegsDetails);
    console.log("analyzeBearBull : lastSale = ", lastSale);

    let bearBullAnalysis = {};
    let openInterestAnalysis = "";
    let totalOpenInterestCalls = 0;
    let totalOpenInterestPuts = 0;
    let pcr = 0;
    let pcrAnalysis = "";
    let iv = "";
    let optionDeltaDistribution = "";
    let skewnessStrikePrices = "";
    let greeks = "";
    let strikeProximity = "";
    let bidAskSpread = "";

    for (let detInd = 0; detInd < optionsLegsDetails.length; detInd++) {
      if (optionsLegsDetails[detInd]) {
        if (optionsLegsDetails[detInd].type === "call") {
          if (optionsLegsDetails[detInd].openInterest) {
            totalOpenInterestCalls += optionsLegsDetails[detInd].openInterest;
          }
        } else {
          if (optionsLegsDetails[detInd].openInterest) {
            totalOpenInterestPuts += optionsLegsDetails[detInd].openInterest;
          }
        }
      }
    }

    if (totalOpenInterestCalls > totalOpenInterestPuts) {
      openInterestAnalysis = "Bullish";
    } else if (totalOpenInterestCalls < totalOpenInterestPuts) {
      openInterestAnalysis = "Bearish";
    } else {
      openInterestAnalysis = "Neutral";
    }

    if (totalOpenInterestCalls > 0 && totalOpenInterestPuts > 0) {
      pcr = totalOpenInterestPuts / totalOpenInterestCalls;
      if (pcr > 1) {
        pcrAnalysis = "Bearish";
      } else if (pcr < 1) {
        pcrAnalysis = "Bullish";
      } else {
        pcrAnalysis = "Neutral";
      }
    }

    iv = analyzeIvBearBull(optionsLegsDetails);
    optionDeltaDistribution = analyzeOptionDeltaDistribution(optionsLegsDetails);
    skewnessStrikePrices = analyzeSkewnessStrikePrices(optionsLegsDetails);
    greeks = analyzeGreeks(optionsLegsDetails);
    strikeProximity = strikeProximityBearBull(optionsLegsDetails, lastSale);
    bidAskSpread = analyzeBidAskSpread(optionsLegsDetails);

    bearBullAnalysis.openInterest = openInterestAnalysis;
    bearBullAnalysis.pcr = pcrAnalysis;
    bearBullAnalysis.iv = iv;
    bearBullAnalysis.optionDeltaDistribution = optionDeltaDistribution;
    bearBullAnalysis.skewnessStrikePrices = skewnessStrikePrices;
    bearBullAnalysis.greeks = greeks;
    bearBullAnalysis.strikeProximity = strikeProximity;
    bearBullAnalysis.bidAskSpread = bidAskSpread;
    bearBullAnalysis.overAll = overallSentimentBullBear(bearBullAnalysis);

    console.log("analyzeBearBull : bearBullAnalysis = ", bearBullAnalysis);
    return bearBullAnalysis;
  }

  const overallSentimentBullBear = (bearBullAnalysis) => {
    console.log("Executing overallSentimentBullBear");
    const sentimentScores = {
      "Bullish": 1,
      "Bearish": -1,
      "Neutral": 0
    };

    let analysisWeights = {
      "greeksWeight": 2,
      "strikeProximityWeight": 1.5,
      "openInterestWeight": 1,
      "optionDeltaDistributionWeight": 1.5,
      "pcrWeight": 1,
      "ivWeight": 1.5,
      "skewnessStrikePrices": 1,
      "bidAskSpreadWeight": 1.25
    }

    let totalScore = 0;
    totalScore += sentimentScores[bearBullAnalysis.greeks] * analysisWeights.greeksWeight;
    totalScore += sentimentScores[bearBullAnalysis.strikeProximity] * analysisWeights.strikeProximityWeight;
    totalScore += sentimentScores[bearBullAnalysis.openInterest] * analysisWeights.openInterestWeight;
    totalScore += sentimentScores[bearBullAnalysis.optionDeltaDistribution] * analysisWeights.optionDeltaDistributionWeight;
    totalScore += sentimentScores[bearBullAnalysis.pcr] * analysisWeights.pcrWeight;
    totalScore += sentimentScores[bearBullAnalysis.iv] * analysisWeights.ivWeight;
    totalScore += sentimentScores[bearBullAnalysis.skewnessStrikePrices] * analysisWeights.skewnessStrikePrices;
    totalScore += sentimentScores[bearBullAnalysis.bidAskSpread] * analysisWeights.bidAskSpreadWeight;
    console.log("overallSentimentBullBear : totalScore = ", totalScore);

    if (totalScore > 0) {
      return "Bullish";
    } else if (totalScore < 0) {
      return "Bearish";
    } else {
      return "Neutral";
    }
  };

  const analyzeBidAskSpread = (optionsLegsDetails) => {
    if (!Array.isArray(optionsLegsDetails) || optionsLegsDetails.length === 0) {
      return 'Neutral';
    }

    const calls = optionsLegsDetails.filter(option => option.type === 'call' && option.bid !== undefined && option.ask !== undefined);
    const puts = optionsLegsDetails.filter(option => option.type === 'put' && option.bid !== undefined && option.ask !== undefined);

    if (calls.length === 0 || puts.length === 0) {
      return 'Neutral';
    }

    const averageCallSpread = calls.reduce((sum, option) => sum + (option.ask - option.bid), 0) / calls.length;
    const averagePutSpread = puts.reduce((sum, option) => sum + (option.ask - option.bid), 0) / puts.length;

    if (averageCallSpread < averagePutSpread) {
      return 'Bullish';
    } else if (averagePutSpread < averageCallSpread) {
      return 'Bearish';
    } else {
      return 'Neutral';
    }
  };

  const strikeProximityBearBull = (optionsLegsDetails, underlyingPrice) => {
    let atmStrike = optionsLegsDetails.reduce((prev, curr) => {
      return Math.abs(curr.strike - underlyingPrice) < Math.abs(prev.strike - underlyingPrice)
        ? curr
        : prev;
    });

    const nearAtmCalls = optionsLegsDetails.filter(
      (option) =>
        option.type === "call" &&
        Math.abs(option.strike - atmStrike.strike) <= 5
    );

    const nearAtmPuts = optionsLegsDetails.filter(
      (option) =>
        option.type === "put" &&
        Math.abs(option.strike - atmStrike.strike) <= 5
    );

    const totalCallOpenInterest = nearAtmCalls.reduce(
      (sum, option) => sum + option.openInterest,
      0
    );
    const totalPutOpenInterest = nearAtmPuts.reduce(
      (sum, option) => sum + option.openInterest,
      0
    );

    if (totalCallOpenInterest > totalPutOpenInterest) {
      return "Bullish";
    } else if (totalPutOpenInterest > totalCallOpenInterest) {
      return "Bearish";
    } else {
      return "Neutral";
    }
  };

  const analyzeGreeks = (optionsLegsDetails) => {
    let totalCallDelta = 0;
    let totalPutDelta = 0;
    let totalCallGamma = 0;
    let totalPutGamma = 0;
    let totalCallVega = 0;
    let totalPutVega = 0;

    optionsLegsDetails.forEach((option) => {
      if (option.type === 'call') {
        if (option.delta) totalCallDelta += option.delta;
        if (option.gamma) totalCallGamma += option.gamma;
        if (option.vega) totalCallVega += option.vega;
      } else if (option.type === 'put') {
        if (option.delta) totalPutDelta += option.delta;
        if (option.gamma) totalPutGamma += option.gamma;
        if (option.vega) totalPutVega += option.vega;
      }
    });

    const isBullish =
      totalCallDelta > Math.abs(totalPutDelta) &&
      totalCallGamma > totalPutGamma &&
      totalCallVega > totalPutVega;

    const isBearish =
      totalPutDelta < -Math.abs(totalCallDelta) &&
      totalPutGamma > totalCallGamma &&
      totalPutVega > totalCallVega;

    if (isBullish) {
      return 'Bullish';
    } else if (isBearish) {
      return 'Bearish';
    } else {
      return 'Neutral';
    }
  };

  const analyzeSkewnessStrikePrices = (optionsLegsDetails) => {
    if (!Array.isArray(optionsLegsDetails) || optionsLegsDetails.length === 0) {
      return 'Neutral';
    }

    const calls = optionsLegsDetails.filter(option => option.type === 'call' && typeof option.impliedVolatility === 'number');
    const puts = optionsLegsDetails.filter(option => option.type === 'put' && typeof option.impliedVolatility === 'number');

    const averageCallIV = calls.reduce((sum, option) => sum + option.impliedVolatility, 0) / calls.length;
    const averagePutIV = puts.reduce((sum, option) => sum + option.impliedVolatility, 0) / puts.length;

    const skew = averagePutIV - averageCallIV;

    if (skew > 0.05) {
      return 'Bearish';
    } else if (skew < -0.05) {
      return 'Bullish';
    } else {
      return 'Neutral';
    }
  };

  function analyzeOptionDeltaDistribution(optionsLegsDetails) {
    let totalCallDelta = 0;
    let totalPutDelta = 0;

    optionsLegsDetails.forEach((option) => {
      if (option.type === "call") {
        if (option.delta) {
          totalCallDelta += option.delta;
        }
      } else if (option.type === "put") {
        if (option.delta) {
          totalPutDelta += option.delta;
        }
      }
    });

    const netDelta = totalCallDelta + totalPutDelta;

    if (netDelta > 0.5) {
      return "Bullish";
    } else if (netDelta < -0.5) {
      return "Bearish";
    } else {
      return "Neutral";
    }
  }

  const analyzeIvBearBull = (options) => {
    let totalCallIV = 0;
    let totalPutIV = 0;
    let callCount = 0;
    let putCount = 0;

    options.forEach((option) => {
      if (option.type === "call") {
        if (option.impliedVolatility) {
          totalCallIV += option.impliedVolatility;
          callCount += 1;
        }
      } else if (option.type === "put") {
        if (option.impliedVolatility) {
          totalPutIV += option.impliedVolatility;
          putCount += 1;
        }
      }
    });

    const averageCallIV = callCount > 0 ? totalCallIV / callCount : 0;
    const averagePutIV = putCount > 0 ? totalPutIV / putCount : 0;

    if (averageCallIV < averagePutIV) {
      return "Bearish";
    } else if (averageCallIV > averagePutIV) {
      return "Bullish";
    } else {
      return "Neutral";
    }
  };

  const handleSubmit = () => {
    console.log("Executing handleSubmit");

    if (!security.current || !days.current) {
      setError("Please fill in all fields");
      return;
    }

    setError('');
    setSubmitted(true);
    setData({ security: security.current, days: days.current });
  };

  const handleReset = () => {
    console.log("Executing handleReset");
    security.current = "";
    days.current = "30";
    setData({ security: "", days: "30" });
    setError('');
    setDisplayMessage('');
    setMarketSentiment(null);
    setSubmitted(false);
  };

  const handleInputChange = (field, value) => {
    console.log("handleInputChange : field = " + field + ", value = " + value);
    if (field === "security") {
      security.current = value;
    } else if (field === "days") {
      days.current = value;
    }
    setData(prevData => ({
      ...prevData,
      [field]: value
    }));
  };

  // Helper function to categorize methods by weight
  const getMethodCategory = (methodName) => {
    const highWeightMethods = ["greeks"]; // weight 2
    const mediumWeightMethods = ["strikeProximity", "optionDeltaDistribution", "iv"]; // weight 1.5
    const mediumLowMethods = ["bidAskSpread", "openInterest", "pcr", "skewnessStrikePrices"]; // weight 1-1.25

    if (highWeightMethods.includes(methodName)) return "High";
    if (mediumWeightMethods.includes(methodName)) return "Medium";
    return "Low";
  };

  const getMethodDisplayName = (methodName) => {
    const displayNames = {
      openInterest: "Open Interest",
      pcr: "Put/Call Ratio (PCR)",
      iv: "Implied Volatility",
      optionDeltaDistribution: "Option Delta Distribution",
      skewnessStrikePrices: "Skewness Strike Prices",
      greeks: "Greeks (Delta/Gamma/Vega)",
      strikeProximity: "Strike Proximity",
      bidAskSpread: "Bid-Ask Spread"
    };
    return displayNames[methodName] || methodName;
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === "Bullish") return "#90EE90"; // light green
    if (sentiment === "Bearish") return "#FFB6C6"; // light red/pink
    return "#D3D3D3"; // light gray for neutral
  };

  return (
    <AppLayout signOut={signOut}>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Page Title */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'rgba(0, 0, 0, 0.9)', marginBottom: '8px' }}>
            Market Direction Analysis
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(107, 114, 128, 1)' }}>
            Analyze market sentiment using options data
          </p>
        </div>

        {/* Input Form Card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid rgba(229, 231, 235, 1)',
          padding: '24px',
          marginBottom: '24px',
          maxWidth: '600px'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(55, 65, 81, 1)'
            }}>
              Security (Symbol)
            </label>
            <input
              type="text"
              value={data.security}
              onChange={(e) => handleInputChange("security", e.target.value.toUpperCase())}
              placeholder="e.g., AAPL, SPY, QQQ"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(55, 65, 81, 1)'
            }}>
              Days to Expiration
            </label>
            <input
              type="number"
              value={data.days}
              onChange={(e) => handleInputChange("days", e.target.value)}
              placeholder="e.g., 30"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              onClick={handleSubmit}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.3)';
              }}
            >
              Analyze
            </button>
            <button
              onClick={handleReset}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              }}
            >
              Reset
            </button>
          </div>

          {error && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}

          {displayMessage && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(59, 130, 246, 0.1)',
              color: '#3b82f6',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {displayMessage}
            </div>
          )}
        </div>

        {/* Results Card */}
        {marketSentiment && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(229, 231, 235, 1)',
            padding: '24px',
            marginBottom: '24px'
          }}>
            {/* Overall Sentiment */}
            <div style={{
              textAlign: 'center',
              marginBottom: '32px',
              padding: '20px',
              backgroundColor: getSentimentColor(marketSentiment.overAll),
              borderRadius: '12px',
              border: '2px solid rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: 'rgba(0, 0, 0, 0.9)',
                margin: 0
              }}>
                Overall Direction: {marketSentiment.overAll}
              </h2>
            </div>

            {/* Analysis Methods */}
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'rgba(0, 0, 0, 0.9)',
              marginBottom: '16px'
            }}>
              Analysis Methods
            </h3>

            <div style={{ display: 'grid', gap: '12px' }}>
              {Object.entries(marketSentiment).map(([method, sentiment]) => {
                if (method === "overAll") return null;

                const category = getMethodCategory(method);
                const displayName = getMethodDisplayName(method);

                return (
                  <div
                    key={method}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      backgroundColor: getSentimentColor(sentiment),
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.9)' }}>
                        {displayName}
                      </strong>
                      <span style={{
                        marginLeft: '12px',
                        fontSize: '12px',
                        padding: '4px 10px',
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        {category} Weight
                      </span>
                    </div>
                    <div style={{
                      fontWeight: '700',
                      fontSize: '16px',
                      color: 'rgba(0, 0, 0, 0.9)'
                    }}>
                      {sentiment}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Note */}
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              fontSize: '14px',
              color: 'rgba(55, 65, 81, 1)'
            }}>
              <strong>Note:</strong> The overall direction is calculated using a weighted average of all methods.
              High weight methods (Greeks) have more influence on the final result.
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default MarketDirection;
